import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/config";

// Appearance configured in Stripe's Checkout Studio.
const APPEARANCE = {
  theme: "stripe",
  labels: "auto",
  inputs: "spaced",
  variables: {
    borderRadius: "4px",
    colorBackground: "#ffffff",
    colorDanger: "#df1b41",
    colorPrimary: "#0570de",
    colorSuccess: "#00c853",
    colorText: "#30313d",
    fontFamily: "default",
    fontSizeBase: "16px",
    spacingUnit: "4px",
  },
};

// Stripe's embedded Checkout Form for subscribing to `planKey`. Stripe.js
// itself is loaded from js.stripe.com in public/index.html (never bundled).
// On successful payment Stripe redirects to the session's return_url
// (/settings/billing?checkout=success), and the webhook activates the plan.
const StripeCheckoutForm = ({ planKey, publishableKey }) => {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let form = null;

    const start = async () => {
      if (!window.Stripe) {
        throw new Error("Stripe couldn't load. Check your connection or disable content blockers and try again.");
      }
      const stripe = window.Stripe(publishableKey, { betas: ["custom_checkout_payment_form_1"] });

      // Fetch the session first so a server-side problem (bad plan, billing
      // not configured) shows our own error instead of a blank form.
      const { data } = await axios.post(`${API}/billing/create-checkout-session`, { plan: planKey });
      if (cancelled) return;

      const checkout = stripe.initCheckoutFormSdk({
        clientSecret: Promise.resolve(data.client_secret),
        appearance: APPEARANCE,
      });

      form = checkout.createForm({ layout: "expanded" });
      form.mount(containerRef.current);

      const loadActionsResult = await checkout.loadActions();
      if (cancelled) return;
      if (loadActionsResult.type !== "success") {
        throw new Error(loadActionsResult.error?.message || "Couldn't load the payment form.");
      }

      form.on("confirm", async (event) => {
        setError("");
        try {
          const result = await loadActionsResult.actions.confirm({ formConfirmEvent: event });
          if (result?.type === "error") {
            setError(result.error?.message || "Payment couldn't be completed. Please try again.");
          }
        } catch (err) {
          console.error("Payment confirmation error:", err);
          setError(err?.message || "Payment couldn't be completed. Please try again.");
        }
      });

      setStatus("ready");
    };

    start().catch((err) => {
      if (cancelled) return;
      console.error("Checkout form error:", err);
      setError(err.response?.data?.detail || err.message || "Couldn't start checkout. Please try again.");
      setStatus("error");
    });

    return () => {
      cancelled = true;
      // Tear down the Stripe iframe when the dialog closes (or React
      // StrictMode re-runs this effect in development).
      try {
        if (form?.destroy) form.destroy();
        else if (form?.unmount) form.unmount();
      } catch {
        /* already gone */
      }
    };
  }, [planKey, publishableKey]);

  return (
    <div>
      {status === "loading" && <p className="text-sm text-zinc-500 py-8 text-center">Loading secure payment form…</p>}
      {error && (
        <div className="border-l-2 border-red-500 bg-red-50 text-red-700 text-sm px-4 py-3 mb-4" data-testid="checkout-error">
          {error}
        </div>
      )}
      <div id="checkout-form" ref={containerRef} data-testid="stripe-checkout-form" />
    </div>
  );
};

export default StripeCheckoutForm;
